import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, ChevronRight, ChevronLeft, Loader2, Globe, Lock, Mail, User, Zap, Shield, Database, AlertCircle } from "lucide-react";

type Step = 1 | 2 | 3 | 4;

interface FormData {
  siteName: string;
  tagline: string;
  contactEmail: string;
  adminEmail: string;
  adminPassword: string;
  adminConfirm: string;
}

type CheckStatus = "checking" | "ok" | "error";

interface Check {
  label: string;
  status: CheckStatus;
}

const STEP_LABELS = ["Welcome", "Site Setup", "Admin Account", "Install"];

function StepIndicator({ current }: { current: Step }) {
  return (
    <div className="flex items-center justify-center gap-0 mb-10">
      {STEP_LABELS.map((label, i) => {
        const step = (i + 1) as Step;
        const done = current > step;
        const active = current === step;
        return (
          <div key={step} className="flex items-center">
            <div className="flex flex-col items-center">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                done ? "bg-primary text-primary-foreground shadow-lg shadow-primary/30"
                : active ? "bg-primary text-primary-foreground ring-4 ring-primary/20"
                : "bg-gray-100 text-gray-400"
              }`}>
                {done ? <CheckCircle2 size={16} /> : step}
              </div>
              <span className={`text-xs mt-1.5 font-medium transition-colors ${active ? "text-primary" : done ? "text-gray-600" : "text-gray-400"}`}>
                {label}
              </span>
            </div>
            {i < STEP_LABELS.length - 1 && (
              <div className={`w-16 h-0.5 mx-1 mb-5 transition-colors duration-500 ${done ? "bg-primary" : "bg-gray-200"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function Step1Welcome({ onNext, checks }: { onNext: () => void; checks: Check[] }) {
  const allOk = checks.every(c => c.status === "ok");
  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Zap className="w-8 h-8 text-primary" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome to Eliada Sports Agency</h2>
        <p className="text-gray-500 text-sm max-w-sm mx-auto">This wizard will guide you through the initial setup. It only takes a couple of minutes.</p>
      </div>

      <div className="bg-gray-50 rounded-2xl p-5 space-y-3">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">System Requirements</p>
        {checks.map((c, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
              c.status === "ok" ? "bg-emerald-100" : c.status === "error" ? "bg-red-100" : "bg-gray-100"
            }`}>
              {c.status === "checking" && <Loader2 size={12} className="animate-spin text-gray-400" />}
              {c.status === "ok" && <CheckCircle2 size={12} className="text-emerald-600" />}
              {c.status === "error" && <AlertCircle size={12} className="text-red-500" />}
            </div>
            <span className={`text-sm font-medium ${c.status === "ok" ? "text-gray-700" : c.status === "error" ? "text-red-600" : "text-gray-400"}`}>
              {c.label}
            </span>
          </div>
        ))}
      </div>

      <button
        onClick={onNext}
        disabled={!allOk}
        className="w-full flex items-center justify-center gap-2 py-3 px-6 bg-primary text-primary-foreground font-bold rounded-xl hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        Get Started <ChevronRight size={16} />
      </button>
    </motion.div>
  );
}

function Step2Site({ data, onChange, onNext, onBack }: {
  data: FormData; onChange: (k: keyof FormData, v: string) => void; onNext: () => void; onBack: () => void;
}) {
  const inp = "w-full px-4 py-3 border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-gray-50 focus:bg-white transition";
  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5">
      <div>
        <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-3">
          <Globe className="w-6 h-6 text-primary" />
        </div>
        <h2 className="text-xl font-bold text-gray-900">Site Configuration</h2>
        <p className="text-gray-500 text-sm mt-1">Set up your public-facing site information.</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Site Name <span className="text-red-500">*</span></label>
          <input className={inp} value={data.siteName} onChange={e => onChange("siteName", e.target.value)} placeholder="e.g. Eliada Sports Agency" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Tagline</label>
          <input className={inp} value={data.tagline} onChange={e => onChange("tagline", e.target.value)} placeholder="e.g. Bridging Talent & Global Opportunity" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Contact Email</label>
          <input className={inp} type="email" value={data.contactEmail} onChange={e => onChange("contactEmail", e.target.value)} placeholder="e.g. hello@youragency.com" />
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <button onClick={onBack} className="flex-1 flex items-center justify-center gap-2 py-3 px-4 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors">
          <ChevronLeft size={15} /> Back
        </button>
        <button onClick={onNext} disabled={!data.siteName.trim()} className="flex-[2] flex items-center justify-center gap-2 py-3 px-6 bg-primary text-primary-foreground font-bold rounded-xl hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          Continue <ChevronRight size={16} />
        </button>
      </div>
    </motion.div>
  );
}

function Step3Admin({ data, onChange, onNext, onBack, error }: {
  data: FormData; onChange: (k: keyof FormData, v: string) => void; onNext: () => void; onBack: () => void; error: string;
}) {
  const [show, setShow] = useState(false);
  const valid = data.adminEmail.trim() && data.adminPassword.length >= 8 && data.adminPassword === data.adminConfirm;
  const inp = "w-full px-4 py-3 border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-gray-50 focus:bg-white transition";
  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5">
      <div>
        <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-3">
          <User className="w-6 h-6 text-primary" />
        </div>
        <h2 className="text-xl font-bold text-gray-900">Admin Account</h2>
        <p className="text-gray-500 text-sm mt-1">Set your login credentials for the admin dashboard.</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Admin Email <span className="text-red-500">*</span></label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input className={`${inp} pl-9`} type="email" value={data.adminEmail} onChange={e => onChange("adminEmail", e.target.value)} placeholder="admin@youragency.com" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Password <span className="text-red-500">*</span></label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input className={`${inp} pl-9 pr-10`} type={show ? "text" : "password"} value={data.adminPassword} onChange={e => onChange("adminPassword", e.target.value)} placeholder="Min. 8 characters" />
            <button type="button" onClick={() => setShow(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs font-semibold">{show ? "Hide" : "Show"}</button>
          </div>
          {data.adminPassword && data.adminPassword.length < 8 && (
            <p className="text-xs text-red-500 mt-1">Must be at least 8 characters</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Confirm Password <span className="text-red-500">*</span></label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input className={`${inp} pl-9`} type="password" value={data.adminConfirm} onChange={e => onChange("adminConfirm", e.target.value)} placeholder="Repeat password" />
          </div>
          {data.adminConfirm && data.adminPassword !== data.adminConfirm && (
            <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2.5 bg-red-50 border border-red-100 rounded-xl p-3.5 text-sm text-red-700">
          <AlertCircle size={15} className="shrink-0 mt-0.5" /> {error}
        </div>
      )}

      <div className="flex gap-3 pt-2">
        <button onClick={onBack} className="flex-1 flex items-center justify-center gap-2 py-3 px-4 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors">
          <ChevronLeft size={15} /> Back
        </button>
        <button onClick={onNext} disabled={!valid} className="flex-[2] flex items-center justify-center gap-2 py-3 px-6 bg-primary text-primary-foreground font-bold rounded-xl hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          Install Now <Zap size={15} />
        </button>
      </div>
    </motion.div>
  );
}

function Step4Installing({ installing, done, adminUrl }: { installing: boolean; done: boolean; adminUrl: string }) {
  const steps = [
    { label: "Connecting to database", icon: Database },
    { label: "Applying site settings", icon: Globe },
    { label: "Securing admin credentials", icon: Shield },
    { label: "Finalizing installation", icon: CheckCircle2 },
  ];

  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6 text-center">
      {!done ? (
        <>
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-1">Installing…</h2>
            <p className="text-gray-500 text-sm">Please wait while we set everything up.</p>
          </div>
          <div className="bg-gray-50 rounded-2xl p-5 text-left space-y-3">
            {steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.4 }}
                  className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Icon size={12} className="text-primary" />
                  </div>
                  <span className="text-sm text-gray-600">{s.label}</span>
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.4 + 0.3 }}
                    className="ml-auto">
                    <CheckCircle2 size={14} className="text-emerald-500" />
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
        </>
      ) : (
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="space-y-6">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-10 h-10 text-emerald-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Installation Complete!</h2>
            <p className="text-gray-500 text-sm">Your site is ready. Sign in to the admin dashboard to start managing your content.</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-800 font-medium">
            The installer has been locked — it cannot be run again.
          </div>
          <a href={adminUrl}
            className="w-full flex items-center justify-center gap-2 py-3.5 px-6 bg-primary text-primary-foreground font-bold rounded-xl hover:bg-primary/90 transition-colors">
            Go to Admin Dashboard <ChevronRight size={16} />
          </a>
        </motion.div>
      )}
    </motion.div>
  );
}

export default function Install() {
  const [step, setStep] = useState<Step>(1);
  const [checks, setChecks] = useState<Check[]>([
    { label: "Database connection", status: "checking" },
    { label: "API server reachable", status: "checking" },
    { label: "Not previously installed", status: "checking" },
  ]);
  const [alreadyInstalled, setAlreadyInstalled] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState<FormData>({
    siteName: "Eliada Sports Agency",
    tagline: "Bridging Talent & Global Opportunity",
    contactEmail: "",
    adminEmail: "",
    adminPassword: "",
    adminConfirm: "",
  });

  const adminBase = window.location.origin.replace(/:\d+/, `:${window.location.port || "80"}`);
  const adminUrl = `${adminBase}/admin/`;

  useEffect(() => {
    async function runChecks() {
      try {
        const r = await fetch("/api/install/status");
        const data = await r.json();

        setChecks([
          { label: "Database connection", status: r.ok ? "ok" : "error" },
          { label: "API server reachable", status: "ok" },
          { label: "Not previously installed", status: data.installed ? "error" : "ok" },
        ]);

        if (data.installed) setAlreadyInstalled(true);
      } catch {
        setChecks([
          { label: "Database connection", status: "error" },
          { label: "API server reachable", status: "error" },
          { label: "Not previously installed", status: "error" },
        ]);
      }
    }
    runChecks();
  }, []);

  const onChange = (k: keyof FormData, v: string) => setForm(f => ({ ...f, [k]: v }));

  const runInstall = async () => {
    setInstalling(true);
    setStep(4);
    setError("");
    try {
      await new Promise(r => setTimeout(r, 1800));
      const res = await fetch("/api/install", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          siteName: form.siteName,
          tagline: form.tagline,
          contactEmail: form.contactEmail,
          adminEmail: form.adminEmail,
          adminPassword: form.adminPassword,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Installation failed");
      await new Promise(r => setTimeout(r, 800));
      setDone(true);
    } catch (e: any) {
      setError(e.message);
      setStep(3);
    } finally {
      setInstalling(false);
    }
  };

  if (alreadyInstalled) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-10 text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Already Installed</h2>
          <p className="text-gray-500 text-sm mb-6">This application has already been set up. The installer is locked.</p>
          <a href={adminUrl} className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-bold rounded-xl hover:bg-primary/90 transition-colors">
            Go to Admin Dashboard <ChevronRight size={15} />
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-yellow-50/30 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <img src="/images/logo.png" alt="Eliada" className="h-14 w-auto mx-auto mb-3 object-contain" onError={e => { (e.target as HTMLImageElement).style.display = "none"; }} />
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Setup Wizard</p>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl shadow-gray-200/60 p-8 border border-gray-100">
          <StepIndicator current={step} />

          <AnimatePresence mode="wait">
            {step === 1 && (
              <Step1Welcome key="s1" checks={checks} onNext={() => setStep(2)} />
            )}
            {step === 2 && (
              <Step2Site key="s2" data={form} onChange={onChange} onNext={() => setStep(3)} onBack={() => setStep(1)} />
            )}
            {step === 3 && (
              <Step3Admin key="s3" data={form} onChange={onChange} onNext={runInstall} onBack={() => setStep(2)} error={error} />
            )}
            {step === 4 && (
              <Step4Installing key="s4" installing={installing} done={done} adminUrl={adminUrl} />
            )}
          </AnimatePresence>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          © {new Date().getFullYear()} Eliada Sports Agency
        </p>
      </div>
    </div>
  );
}
